import { t } from 'i18next';
import { NODE_CATEGORIES } from '../../../../../constants/workflow.constant';
import { getNodeFormIds } from '../utils/graphHelpers';

const categorizeIncomingEdges = (incomingEdges, nodes) => {
  const validCategories = [
    NODE_CATEGORIES.SUBMISSION,
    NODE_CATEGORIES.REVIEW,
    NODE_CATEGORIES.START,
    NODE_CATEGORIES.CHILD_APP,
    NODE_CATEGORIES.CHILD_REJ,
    NODE_CATEGORIES.CHILD_SBK,
  ];

  const categorized = {
    submission: 0,
    review: 0,
    start: 0,
    approve: 0,
    reject: 0,
    sendBack: 0,
    other: 0,
  };

  for (const edge of incomingEdges) {
    const sourceNode = nodes.find((n) => n.id === edge.source);
    const category = sourceNode?.category;

    if (category === NODE_CATEGORIES.SUBMISSION) {
      categorized.submission++;
    } else if (category === NODE_CATEGORIES.REVIEW) {
      categorized.review++;
    } else if (category === NODE_CATEGORIES.START) {
      categorized.start++;
    } else if (category === NODE_CATEGORIES.CHILD_APP) {
      categorized.approve++;
    } else if (category === NODE_CATEGORIES.CHILD_REJ) {
      categorized.reject++;
    } else if (category === NODE_CATEGORIES.CHILD_SBK) {
      categorized.sendBack++;
    } else if (!validCategories.includes(category)) {
      categorized.other++;
    }
  }

  return categorized;
};

const categorizeOutgoingActionEdges = (outgoingEdges, nodes) => {
  return {
    approve: outgoingEdges.filter((edge) => {
      const targetNode = nodes.find((n) => n.id === edge.target);
      return targetNode?.category === NODE_CATEGORIES.CHILD_APP;
    }).length,
    reject: outgoingEdges.filter((edge) => {
      const targetNode = nodes.find((n) => n.id === edge.target);
      return targetNode?.category === NODE_CATEGORIES.CHILD_REJ;
    }).length,
    sendBack: outgoingEdges.filter((edge) => {
      const targetNode = nodes.find((n) => n.id === edge.target);
      return targetNode?.category === NODE_CATEGORIES.CHILD_SBK;
    }).length,
    other: outgoingEdges.filter((edge) => {
      const targetNode = nodes.find((n) => n.id === edge.target);
      return (
        targetNode?.category !== NODE_CATEGORIES.CHILD_APP &&
        targetNode?.category !== NODE_CATEGORIES.CHILD_REJ &&
        targetNode?.category !== NODE_CATEGORIES.CHILD_SBK &&
        targetNode?.category !== NODE_CATEGORIES.CHILD_STA
      );
    }).length,
  };
};

export const filterAssessmentReviewEdgesAndNodes = (
  incomingEdges,
  nodes,
  edges
) => {
  const nodesMap = new Map(nodes.map((n) => [n.id, n]));
  const edgesByTarget = new Map();
  for (const e of edges) {
    if (!edgesByTarget.has(e.target)) {
      edgesByTarget.set(e.target, []);
    }
    edgesByTarget.get(e.target).push(e);
  }

  const assessmentReviewNodeIds = new Set();
  const assessmentReviewEdgeIds = new Set();

  for (const edge of incomingEdges) {
    const sourceNode = nodesMap.get(edge.source);
    if (
      sourceNode?.category === NODE_CATEGORIES.CHILD_APP ||
      sourceNode?.category === NODE_CATEGORIES.CHILD_REJ
    ) {
      const incomingApproveRejectEdges = edgesByTarget.get(sourceNode.id) || [];
      for (const incomingApproveRejectEdge of incomingApproveRejectEdges) {
        const sourceNodeForApproveReject = nodesMap.get(
          incomingApproveRejectEdge.source
        );
        if (
          sourceNodeForApproveReject?.category ===
          NODE_CATEGORIES.ASSESSMENT_REVIEW
        ) {
          assessmentReviewNodeIds.add(sourceNodeForApproveReject.id);
          assessmentReviewEdgeIds.add(edge.id);
        }
      }
    }
  }

  return {
    assessmentReviewNodeIds,
    assessmentReviewEdgeIds,
  };
};

export const validateSubmissionNodeConnections = (nodes, edges) => {
  const submissionNodes = nodes.filter(
    (node) => node.category === NODE_CATEGORIES.SUBMISSION
  );

  for (const submissionNode of submissionNodes) {
    const submissionName =
      submissionNode.data?.formConfig?.name || submissionNode.id;
    const incomingEdges = edges.filter(
      (edge) => edge.target === submissionNode.id
    );
    const outgoingEdges = edges.filter(
      (edge) => edge.source === submissionNode.id
    );

    const incoming = categorizeIncomingEdges(incomingEdges, nodes);

    const normalIncomingCount =
      incoming.submission + incoming.review + incoming.start;
    if (normalIncomingCount > 1) {
      return {
        message: t('workflowValidation.submissionNodeTooManyNormalIncoming', {
          name: submissionName,
        }),
      };
    }

    if (incoming.approve > 1) {
      return {
        message: t('workflowValidation.submissionNodeMaxOneApproveIncoming', {
          name: submissionName,
        }),
      };
    }

    if (incoming.reject > 1) {
      return {
        message: t('workflowValidation.submissionNodeMaxOneRejectIncoming', {
          name: submissionName,
        }),
      };
    }

    if (incoming.other > 0) {
      return {
        message: t('workflowValidation.submissionNodeInvalidIncoming', {
          name: submissionName,
        }),
      };
    }

    if (outgoingEdges.length !== 1) {
      return {
        message: t('workflowValidation.submissionNodeSingleOutgoing', {
          name: submissionName,
        }),
      };
    }
  }

  return null;
};

export const validateReviewNodeConnections = (nodes, edges) => {
  const reviewNodes = nodes.filter(
    (node) => node.category === NODE_CATEGORIES.REVIEW
  );

  for (const reviewNode of reviewNodes) {
    const reviewName =
      reviewNode.data?.formConfig?.name ||
      reviewNode.data?.name ||
      reviewNode.id;
    let incomingEdges = edges.filter((edge) => edge.target === reviewNode.id);
    const outgoingEdges = edges.filter((edge) => edge.source === reviewNode.id);

    const { assessmentReviewNodeIds, assessmentReviewEdgeIds } =
      filterAssessmentReviewEdgesAndNodes(incomingEdges, nodes, edges);

    incomingEdges = incomingEdges.filter(
      (e) => !assessmentReviewEdgeIds.has(e.id)
    );

    if (assessmentReviewNodeIds.size > 1) {
      return {
        message: t('workflowValidation.reviewNodeMultipleSendToAssessment', {
          name: reviewName,
        }),
      };
    }

    const incoming = categorizeIncomingEdges(incomingEdges, nodes);

    const normalIncomingCount =
      incoming.submission + incoming.review + incoming.start;
    if (normalIncomingCount > 1) {
      return {
        message: t('workflowValidation.reviewNodeTooManyNormalIncoming', {
          name: reviewName,
        }),
      };
    }

    if (incoming.approve > 1) {
      return {
        message: t('workflowValidation.reviewNodeMaxOneApproveIncoming', {
          name: reviewName,
        }),
      };
    }

    if (incoming.reject > 1) {
      return {
        message: t('workflowValidation.reviewNodeMaxOneRejectIncoming', {
          name: reviewName,
        }),
      };
    }

    if (incoming.other > 0) {
      return {
        message: t('workflowValidation.reviewNodeInvalidIncoming', {
          name: reviewName,
        }),
      };
    }

    const outgoing = categorizeOutgoingActionEdges(outgoingEdges, nodes);

    if (outgoing.approve > 1) {
      return {
        message: t('workflowValidation.reviewNodeMultipleApprove', {
          name: reviewName,
        }),
      };
    }

    if (outgoing.reject > 1) {
      return {
        message: t('workflowValidation.reviewNodeMultipleReject', {
          name: reviewName,
        }),
      };
    }

    if (outgoing.sendBack > 1) {
      return {
        message: t('workflowValidation.reviewNodeMultipleSendBack', {
          name: reviewName,
        }),
      };
    }

    if (outgoing.other > 0) {
      return {
        message: t('workflowValidation.reviewMustConnectToAction', {
          name: reviewName,
        }),
      };
    }
  }

  return null;
};

export const validateConsecutiveSubmissions = (nodes, edges) => {
  const submissionNodes = nodes.filter(
    (node) => node.category === NODE_CATEGORIES.SUBMISSION
  );

  if (submissionNodes.length <= 1) {
    return null;
  }

  const formIdToNode = new Map();
  for (const node of submissionNodes) {
    const nodeFormIds = getNodeFormIds(node);
    const formNames = node.data?.formConfig?.form_names || {};
    for (const formId of nodeFormIds) {
      if (formIdToNode.has(formId)) {
        const existingNode = formIdToNode.get(formId);
        const existingFormNames =
          existingNode.data?.formConfig?.form_names || {};
        return {
          message: t('workflowValidation.duplicateFormInSubmissions', {
            formName:
              formNames[formId] ||
              existingFormNames[formId] ||
              `Form ${formId}`,
            stage1: existingNode.data?.formConfig?.name || existingNode.id,
            stage2: node.data?.formConfig?.name || node.id,
          }),
        };
      }
      formIdToNode.set(formId, node);
    }
  }

  for (const submissionNode of submissionNodes) {
    const submissionName =
      submissionNode.data?.formConfig?.name || submissionNode.id;

    const outgoingEdges = edges.filter(
      (edge) => edge.source === submissionNode.id
    );

    for (const edge of outgoingEdges) {
      const targetNode = nodes.find((n) => n.id === edge.target);

      if (
        targetNode?.category !== NODE_CATEGORIES.SUBMISSION &&
        targetNode?.category !== NODE_CATEGORIES.REVIEW
      ) {
        return {
          message: t(
            'workflowValidation.submissionMustConnectToSubmissionOrReview',
            { name: submissionName }
          ),
        };
      }
    }
  }

  return null;
};
